import React from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { ColumnDef, CellContext } from '@tanstack/react-table';
import { AccountMovement, Debt } from '../../types';
import type { Language } from '../../contexts/LanguageContext';
import { formatDateTime } from '../../utils/date';
import { getColumnKey } from '../../utils/table';
import { customerAccountAmountColor, formatCustomerAccountAmount } from '../../utils/customerAccountAmount';
import { accountMovementDescription, toEditableDebt } from '../../utils/accountMovement';
import DebtActionsMenu from './DebtActionsMenu';
import { createCustomerNameActionSx } from '../../theme/interactions';
import { DebtMovementIcon, isSaleLinked, movementKind } from './DebtMovementIcon';

export { isSaleLinked, movementKind } from './DebtMovementIcon';

interface DebtColumnOptions {
  t: (key: string) => string;
  language: Language;
  canManageDebts: boolean;
  isSuperAdmin: boolean;
  onCustomerClick?: (debt: AccountMovement) => void;
  onEdit: (debt: AccountMovement) => void;
  onDelete: (debt: AccountMovement) => void;
  onSaleMovementLocked: () => void;
}

export const MovementIcon = DebtMovementIcon;

export function MovementBadge({ debt, t }: { debt: Debt | AccountMovement; t: (key: string) => string }) {
  const kind = movementKind(debt);
  const label = kind === 'payment' ? t('debts.payment') : kind === 'sale' ? t('debts.sale') : t('debts.manualDebt');
  return <Chip label={label} size="small" sx={{ fontWeight: 600, borderRadius: 1, color: kind === 'payment' ? 'success.main' : 'error.main', bgcolor: kind === 'payment' ? 'rgba(35, 96, 68, .10)' : 'rgba(164, 54, 47, .09)' }} />;
}

export function MovementAmount({ debt, t, showTypeLabel = true }: { debt: Debt | AccountMovement; t: (key: string) => string; showTypeLabel?: boolean }) {
  const color = customerAccountAmountColor(debt.amount);
  const kind = movementKind(debt);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', whiteSpace: 'nowrap' }}>
      <Typography sx={{ color, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {formatCustomerAccountAmount(debt.amount)}
      </Typography>
      {showTypeLabel && <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em' }}>
        {kind === 'payment' ? t('debts.payment') : kind === 'sale' ? t('debts.sale') : t('debts.manualDebt')}
      </Typography>}
    </Box>
  );
}

export function createDebtColumns(options: DebtColumnOptions): ColumnDef<AccountMovement>[] {
  const { t, language, canManageDebts, isSuperAdmin, onCustomerClick, onEdit, onDelete, onSaleMovementLocked } = options;
  return [
    { accessorKey: 'customer', header: t('debts.customer'), enableSorting: false,
      cell: ({ row }: CellContext<AccountMovement, unknown>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 155 }}>
          <MovementIcon debt={row.original} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component={onCustomerClick ? 'button' : 'span'}
              type={onCustomerClick ? 'button' : undefined}
              aria-label={onCustomerClick ? `${t('debts.openCustomerAccount')}: ${row.original.customerName || t('common.unknown')}` : undefined}
              onClick={() => onCustomerClick?.(row.original)}
              sx={theme => ({ display: 'block', p: 0, border: 0, bgcolor: 'transparent', color: 'text.primary', textAlign: 'left', ...(onCustomerClick ? createCustomerNameActionSx(theme) : {}) })}
            >
              {row.original.customerName || t('common.unknown')}
            </Typography>
          </Box>
        </Box>
      ) },
    { accessorKey: 'debtType', header: t('debts.movementType'), enableSorting: false,
      cell: ({ row }: CellContext<AccountMovement, unknown>) => <MovementBadge debt={row.original} t={t} /> },
    { accessorKey: 'description', header: t('debts.description'), enableSorting: false,
      cell: ({ row }: CellContext<AccountMovement, unknown>) => <Typography variant="body2" sx={{ maxWidth: 260 }} noWrap title={accountMovementDescription(row.original, t)}>{accountMovementDescription(row.original, t)}</Typography> },
    { accessorKey: 'saleNumber', header: t('debts.saleNumber'), enableSorting: false,
      cell: ({ row }: CellContext<AccountMovement, unknown>) => <Typography variant="body2" color="text.secondary">{row.original.saleNumber || '—'}</Typography> },
    { accessorKey: 'lastActivityDateTime', header: t('debts.transactionDateTime'),
      cell: ({ row }: CellContext<AccountMovement, unknown>) => <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.original.lastActivityDateTime || row.original.transactionDateTime || row.original.transactionDate, language, { dateStyle: 'medium', timeStyle: 'short' })}</Typography> },
    { accessorKey: 'amount', header: t('debts.amount'),
      cell: ({ row }: CellContext<AccountMovement, unknown>) => <MovementAmount debt={row.original} t={t} /> },
    { accessorKey: 'createdByUsername', header: t('debts.createdBy'), enableSorting: false,
      cell: ({ row }: CellContext<AccountMovement, unknown>) => <Typography variant="body2">{row.original.createdByUsername || '—'}</Typography> },
    ...(isSuperAdmin ? [{ accessorKey: 'companyName', header: t('debts.company'), enableSorting: false, cell: ({ row }: CellContext<AccountMovement, unknown>) => <Typography variant="body2">{row.original.companyName || '—'}</Typography> }] : []),
    ...(canManageDebts ? [{ id: 'actions', header: t('common.actions'), enableSorting: false,
      cell: ({ row }: CellContext<AccountMovement, unknown>) => isSaleLinked(row.original) ? (
        <Tooltip title={t('debts.saleMovementReadOnly')}>
          <IconButton onClick={onSaleMovementLocked} aria-label={t('debts.saleMovementReadOnly')}><LockOutlined fontSize="small" /></IconButton>
        </Tooltip>
      ) : <DebtActionsMenu debt={toEditableDebt(row.original)} onEdit={() => onEdit(row.original)} onDelete={() => onDelete(row.original)} /> }] : []),
  ];
}

export function filterDebtColumns<T>(columns: ColumnDef<T>[], visibility: Record<string, boolean>, isSuperAdminOrCanManageDebts: boolean, canManageDebts?: boolean) {
  const isSuperAdmin = canManageDebts === undefined ? true : isSuperAdminOrCanManageDebts;
  const canManage = canManageDebts === undefined ? isSuperAdminOrCanManageDebts : canManageDebts;
  return columns.filter(column => {
    const key = getColumnKey(column);
    if (key === 'companyName' && !isSuperAdmin) return false;
    if (key === 'actions' && !canManage) return false;
    return key == null || visibility[key] !== false;
  });
}
